/**
 * The audio module the games share: sample playback from a manifest over
 * the Synth's one AudioContext and its three buses (sfx, music, ambience),
 * remembered mute and volumes, the mobile unlock, loops with live gain and
 * pitch, and crossfaded music and ambience beds. A name with no decoded
 * file answers false from `play`, and the caller runs its synth recipe
 * instead, so nothing ever goes silent.
 *
 * Manifest (client/public/audio/manifest.json): `{ version, sounds: { name:
 * url | [urls] | { src, gain } } }`. Several urls under one name are
 * variants picked at random. Urls may be paths in the build or absolute
 * (the media host the server's CSP names).
 */

import { storageGet, storageSet } from '../ui/dom.js';
import { STORAGE_KEYS } from '../ui/helpers.js';
import { isSoundName } from './names.js';
import { clamp01, clampPan, getSynth, type Bus, type Synth } from './synth.js';

export interface AudioPrefs {
  muted: boolean;
  master: number;
  music: number;
  sfx: number;
}

export const PREFS_KEY = 'audio:prefs';
export const DEFAULT_PREFS: Readonly<AudioPrefs> = Object.freeze({ muted: false, master: 0.8, music: 0.5, sfx: 1 });
export const MANIFEST_URL = '/audio/manifest.json';
/** A manifest that failed to load is asked for again by the next gesture no sooner than this (ms). */
const MANIFEST_RETRY_MS = 30_000;

export interface Sound {
  urls: string[];
  gain: number;
  /** Seconds: where a looping file jumps back to, and where it jumps from (a bed's crossfaded tail). */
  loopStart?: number;
  loopEnd?: number;
  /** A sting: played once as a bed, not looped. */
  once?: boolean;
}

/**
 * Saved prefs, or the defaults; the old `spellweave.muted` flag carries over
 * when nothing newer was saved.
 */
export function prefsFrom(raw: string | null, legacyMuted: string | null): AudioPrefs {
  const base: AudioPrefs = { ...DEFAULT_PREFS, muted: legacyMuted === '1' };
  if (!raw) return base;
  try {
    const p = JSON.parse(raw) as Partial<Record<keyof AudioPrefs, unknown>>;
    const num = (v: unknown, fallback: number): number => (typeof v === 'number' && Number.isFinite(v) ? clamp01(v) : fallback);
    return {
      muted: typeof p.muted === 'boolean' ? p.muted : base.muted,
      master: num(p.master, base.master),
      music: num(p.music, base.music),
      sfx: num(p.sfx, base.sfx),
    };
  } catch {
    return base;
  }
}

/** The manifest's sounds, dropping anything malformed or unknown (the test lists what was dropped). */
export function parseManifest(raw: unknown): { sounds: Map<string, Sound>; dropped: string[] } {
  const sounds = new Map<string, Sound>();
  const dropped: string[] = [];
  if (typeof raw !== 'object' || raw === null) return { sounds, dropped: ['manifest is not an object'] };
  const list = (raw as { sounds?: unknown }).sounds;
  if (typeof list !== 'object' || list === null) return { sounds, dropped: ['manifest has no sounds'] };
  for (const [name, entry] of Object.entries(list as Record<string, unknown>)) {
    if (!isSoundName(name)) {
      dropped.push(`${name}: not a sound the game asks for`);
      continue;
    }
    let src: unknown = entry;
    let gain = 1;
    let loopStart: number | undefined;
    let loopEnd: number | undefined;
    let once = false;
    if (typeof entry === 'object' && entry !== null && !Array.isArray(entry)) {
      const e = entry as { src?: unknown; gain?: unknown; loopStart?: unknown; loopEnd?: unknown; once?: unknown };
      src = e.src;
      if (typeof e.gain === 'number' && Number.isFinite(e.gain) && e.gain >= 0 && e.gain <= 4) gain = e.gain;
      if (typeof e.loopStart === 'number' && Number.isFinite(e.loopStart) && e.loopStart >= 0) loopStart = e.loopStart;
      if (typeof e.loopEnd === 'number' && Number.isFinite(e.loopEnd) && e.loopEnd > (loopStart ?? 0)) loopEnd = e.loopEnd;
      if (e.once === true) once = true;
    }
    const urls = (Array.isArray(src) ? src : [src]).filter((u): u is string => typeof u === 'string' && u.length > 0 && !/\s/.test(u));
    if (urls.length === 0) {
      dropped.push(`${name}: no url`);
      continue;
    }
    const sound: Sound = { urls, gain };
    if (loopStart !== undefined) sound.loopStart = loopStart;
    if (loopEnd !== undefined) sound.loopEnd = loopEnd;
    if (once) sound.once = true;
    sounds.set(name, sound);
  }
  return { sounds, dropped };
}

export interface PlayOpts {
  /** 0..1 on top of the manifest gain (default 1). */
  vol?: number;
  /** Playback rate (default 1). */
  pitch?: number;
  /** -1..1 (default centre). */
  pan?: number;
  /** Skip when the same name played within this many ms. */
  throttleMs?: number;
}

export interface SampleLoop {
  readonly stopped: boolean;
  setGain(gain: number, rampSeconds?: number): void;
  setPan(pan: number): void;
  setRate(rate: number, rampSeconds?: number): void;
  stop(releaseSeconds?: number): void;
}

interface Bed {
  name: string;
  src: AudioBufferSourceNode;
  gain: GainNode;
  /** A layered bed's second loop, playing in step with the first (`layers`), and both layers' full gains. */
  layer?: { src: AudioBufferSourceNode; gain: GainNode; peak: [number, number] };
}

/** A layered bed's name: its two layers joined by '+'. */
const pairOf = (name: string): [string, string] | null => {
  const i = name.indexOf('+');
  return i < 0 ? null : [name.slice(0, i), name.slice(i + 1)];
};

const BED_FADE = 1.2;
/** Effects sit a touch under the music at equal slider settings. */
const SFX_TRIM = 0.8;
/** Per play: a little pitch and level spread so one recording heard twice is not a machine. */
const PITCH_JITTER = 0.035;
const VOL_JITTER = 0.12;
/** How much of a sample goes to the effects bus's short room (menu sounds stay dry). */
const SAMPLE_REVERB = 0.16;
/** Busy moments: past these, a new one-shot is dropped rather than piled into the limiter. */
const MAX_SAMPLE_VOICES = 20;
const MAX_PER_NAME = 4;
/** Seconds shaved off each end of a looping file: mp3 decoders pad the edges with silence that would click. */
const LOOP_TRIM = 0.035;
/** Music beds run a minute or more each (tens of MB once decoded), so they are fetched when first asked for and dropped after. */
const isLazy = (name: string): boolean => name.startsWith('music.');

/**
 * Loop points: the manifest's, when a bed was built with a crossfaded tail
 * (play 0..loopEnd, then jump to loopStart), else just past the mp3 padding.
 */
function trimLoop(src: AudioBufferSourceNode, sound?: { loopStart?: number; loopEnd?: number }): void {
  const dur = src.buffer?.duration ?? 0;
  if (dur < 0.5) return;
  const start = sound?.loopStart ?? LOOP_TRIM;
  const end = sound?.loopEnd ?? dur - LOOP_TRIM;
  if (end > start && end <= dur) {
    src.loopStart = start;
    src.loopEnd = end;
  }
}

export class GameAudio {
  prefs: AudioPrefs;
  private readonly sounds = new Map<string, Sound>();
  private readonly buffers = new Map<string, AudioBuffer>();
  /** Files on their way: a second ask for one waits for it instead of downloading and decoding it again. */
  private readonly decoding = new Map<string, Promise<void>>();
  private readonly failed = new Set<string>();
  private readonly lastAt = new Map<string, number>();
  private voices = 0;
  private readonly voicesByName = new Map<string, number>();
  private manifestUrl: string | null = null;
  private loaded = false;
  private loadingManifest: Promise<void> | null = null;
  /** After a failed manifest fetch, key presses don't ask again until this time (ms, Date.now). */
  private manifestRetryAt = 0;
  private beds: Record<'music' | 'ambience', Bed | null> = { music: null, ambience: null };
  private wanted: Record<'music' | 'ambience', string | null> = { music: null, ambience: null };
  private duckGain: GainNode | null = null;
  /** Mix of a layered bed: 0 = all its first layer, 1 = all its second (`layers`). */
  private layerMix = 0;
  private visibilityBound = false;

  constructor(private readonly synth: Synth = getSynth()) {
    this.prefs = prefsFrom(storageGet(PREFS_KEY), storageGet(STORAGE_KEYS.muted));
  }

  get muted(): boolean {
    return this.prefs.muted;
  }

  get ready(): boolean {
    return this.synth.ready;
  }

  /** Call from a user gesture: opens the context, applies the saved levels, starts loading the manifest. */
  unlock(): void {
    this.synth.resume();
    this.applyPrefs();
    if (!this.visibilityBound && typeof document !== 'undefined') {
      this.visibilityBound = true;
      // A hidden tab stops making sound; coming back restarts the beds where they left off.
      document.addEventListener('visibilitychange', () => {
        const ctx = this.synth.context;
        if (!ctx) return;
        if (document.hidden) void ctx.suspend().catch(() => { /* already closed */ });
        else void ctx.resume().catch(() => { /* needs a gesture */ });
      });
    }
    if (this.manifestUrl && !this.loaded && !this.loadingManifest && this.synth.context && Date.now() >= this.manifestRetryAt) {
      void this.load(this.manifestUrl);
    }
  }

  private applyPrefs(): void {
    this.synth.setVolume(this.prefs.muted ? 0 : this.prefs.master);
    this.synth.setBusVolume('sfx', this.prefs.sfx * SFX_TRIM);
    this.synth.setBusVolume('music', this.prefs.music);
    this.synth.setBusVolume('ambience', this.prefs.music);
  }

  setPrefs(next: Partial<AudioPrefs>): void {
    this.prefs = { ...this.prefs, ...next };
    storageSet(PREFS_KEY, JSON.stringify(this.prefs));
    // The old flag stays in step for anything that still reads it.
    storageSet(STORAGE_KEYS.muted, this.prefs.muted ? '1' : '0');
    this.applyPrefs();
  }

  setMuted(muted: boolean): void {
    this.setPrefs({ muted });
  }

  toggleMute(): boolean {
    this.setMuted(!this.prefs.muted);
    return this.prefs.muted;
  }

  /** Point at the manifest; it loads once the context exists (after the first gesture). */
  use(manifestUrl: string): void {
    this.manifestUrl = manifestUrl;
    if (this.synth.context) void this.load(manifestUrl);
  }

  /** Fetch the manifest and decode every file it names. Safe to call again; a second call waits for the first. */
  load(manifestUrl: string): Promise<void> {
    if (this.loadingManifest) return this.loadingManifest;
    this.loadingManifest = (async () => {
      try {
        const res = await fetch(manifestUrl, { cache: 'no-cache' });
        if (!res.ok) {
          this.manifestRetryAt = Date.now() + MANIFEST_RETRY_MS;
          return;
        }
        const { sounds } = parseManifest(await res.json());
        for (const [name, sound] of sounds) this.sounds.set(name, sound);
        this.loaded = true;
        const urls = new Set<string>();
        for (const [name, s] of sounds) if (!isLazy(name)) for (const u of s.urls) urls.add(u);
        await Promise.all([...urls].map((u) => this.decode(u)));
        // A bed asked for while the manifest was still on its way starts now.
        for (const kind of ['music', 'ambience'] as const) {
          const name = this.wanted[kind];
          if (name && this.beds[kind]?.name !== name) this.bed(kind, name);
        }
      } catch {
        /* no manifest: the synths carry the game */
        if (!this.loaded) this.manifestRetryAt = Date.now() + MANIFEST_RETRY_MS;
      } finally {
        this.loadingManifest = null;
      }
    })();
    return this.loadingManifest;
  }

  private decode(url: string): Promise<void> {
    const ctx = this.synth.context;
    if (!ctx || this.buffers.has(url) || this.failed.has(url)) return Promise.resolve();
    const pending = this.decoding.get(url);
    if (pending) return pending;
    const job = (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(String(res.status));
        const buf = await ctx.decodeAudioData(await res.arrayBuffer());
        this.buffers.set(url, buf);
      } catch {
        this.failed.add(url);
      } finally {
        this.decoding.delete(url);
      }
    })();
    this.decoding.set(url, job);
    return job;
  }

  /** True when `name` has at least one decoded file. */
  has(name: string): boolean {
    const s = this.sounds.get(name);
    return s !== undefined && s.urls.some((u) => this.buffers.has(u));
  }

  /** How many names have a file behind them (the settings panel shows it); a lazy bed counts once the manifest names it. */
  get loadedCount(): number {
    let n = 0;
    for (const [name, s] of this.sounds) if (this.has(name) || (isLazy(name) && s.urls.some((u) => !this.failed.has(u)))) n++;
    return n;
  }

  private pick(name: string): { buffer: AudioBuffer; gain: number; sound: Sound } | null {
    const s = this.sounds.get(name);
    if (!s) return null;
    const ready = s.urls.filter((u) => this.buffers.has(u));
    if (ready.length === 0) return null;
    const url = ready[Math.floor(Math.random() * ready.length)] as string;
    return { buffer: this.buffers.get(url) as AudioBuffer, gain: s.gain, sound: s };
  }

  /** Play a one-shot on the sfx bus. False when there is no file for it (the caller's synth takes over). */
  play(name: string, opts: PlayOpts = {}): boolean {
    const ctx = this.synth.context;
    const bus = this.synth.bus('sfx');
    if (!ctx || !bus || !this.synth.ready) return false;
    const picked = this.pick(name);
    if (!picked) return false;
    if (opts.throttleMs) {
      const now = performance.now();
      const last = this.lastAt.get(name) ?? -Infinity;
      if (now - last < opts.throttleMs) return true;
      this.lastAt.set(name, now);
    }
    // Over the cap the newest copy is the one nobody misses; true, so the synth does not step in either.
    const sameName = this.voicesByName.get(name) ?? 0;
    if (this.voices >= MAX_SAMPLE_VOICES || sameName >= MAX_PER_NAME) return true;
    const src = ctx.createBufferSource();
    src.buffer = picked.buffer;
    const pitch = (opts.pitch ?? 1) * (1 + (Math.random() * 2 - 1) * PITCH_JITTER);
    src.playbackRate.value = Math.max(0.25, Math.min(4, pitch));
    const g = ctx.createGain();
    g.gain.value = clamp01(opts.vol ?? 1) * picked.gain * (1 - Math.random() * VOL_JITTER);
    let out: AudioNode = g;
    if (opts.pan !== undefined && typeof ctx.createStereoPanner === 'function') {
      const pan = ctx.createStereoPanner();
      pan.pan.value = clampPan(opts.pan);
      g.connect(pan);
      out = pan;
    }
    src.connect(g);
    out.connect(bus);
    const room = this.synth.reverbSend;
    if (room && !name.startsWith('ui.')) {
      const send = ctx.createGain();
      send.gain.value = SAMPLE_REVERB;
      out.connect(send);
      send.connect(room);
    }
    this.voices++;
    this.voicesByName.set(name, sameName + 1);
    src.onended = () => {
      this.voices = Math.max(0, this.voices - 1);
      this.voicesByName.set(name, Math.max(0, (this.voicesByName.get(name) ?? 1) - 1));
    };
    src.start();
    return true;
  }

  /** A looping sample on the sfx bus with live gain, pan and rate; null when there is no file. */
  loop(name: string, opts: { gain?: number; pan?: number; rate?: number } = {}): SampleLoop | null {
    const ctx = this.synth.context;
    const bus = this.synth.bus('sfx');
    if (!ctx || !bus || !this.synth.ready) return null;
    const picked = this.pick(name);
    if (!picked) return null;
    const src = ctx.createBufferSource();
    src.buffer = picked.buffer;
    src.loop = true;
    trimLoop(src, picked.sound);
    src.playbackRate.value = opts.rate ?? 1;
    const g = ctx.createGain();
    g.gain.value = 0;
    g.gain.setTargetAtTime(clamp01(opts.gain ?? 1) * picked.gain, ctx.currentTime, 0.04);
    let out: AudioNode = g;
    let pan: StereoPannerNode | null = null;
    if (typeof ctx.createStereoPanner === 'function') {
      pan = ctx.createStereoPanner();
      pan.pan.value = clampPan(opts.pan ?? 0);
      g.connect(pan);
      out = pan;
    }
    src.connect(g);
    out.connect(bus);
    src.start();
    let stopped = false;
    const scale = picked.gain;
    return {
      get stopped() { return stopped; },
      setGain: (gain, ramp = 0.05) => { if (!stopped) g.gain.setTargetAtTime(clamp01(gain) * scale, ctx.currentTime, ramp); },
      setPan: (p) => { if (pan && !stopped) pan.pan.setTargetAtTime(clampPan(p), ctx.currentTime, 0.05); },
      setRate: (rate, ramp = 0.05) => { if (!stopped) src.playbackRate.setTargetAtTime(Math.max(0.25, Math.min(4, rate)), ctx.currentTime, ramp); },
      stop: (release = 0.08) => {
        if (stopped) return;
        stopped = true;
        g.gain.setTargetAtTime(0, ctx.currentTime, release / 3);
        src.stop(ctx.currentTime + release + 0.05);
      },
    };
  }

  /**
   * Crossfade a bed on the music or ambience bus; `null` fades the current one out.
   * The same name again is left playing. False when there is no file (nothing changes).
   */
  bed(kind: 'music' | 'ambience', name: string | null, fade = BED_FADE): boolean {
    this.wanted[kind] = name;
    const ctx = this.synth.context;
    const bus = this.synth.bus(kind);
    if (!ctx || !bus || !this.synth.ready) return false;
    const current = this.beds[kind];
    if (current && current.name === name) return true;
    const pair = name !== null ? pairOf(name) : null;
    if (pair && (!this.has(pair[0]) || !this.has(pair[1]))) {
      // a layered bed starts once both layers are decoded (they must start together to stay in step)
      void Promise.all(pair.map((n) => this.decodeLazy(n))).then(() => {
        if (this.wanted[kind] === name && this.beds[kind]?.name !== name && this.has(pair[0]) && this.has(pair[1])) this.bed(kind, name, fade);
      });
      return false;
    }
    if (name !== null && !pair && !this.has(name)) {
      // Not decoded yet (a lazy bed, or the manifest is still loading): the old bed plays on
      // until this one is ready, then the switch happens if it is still wanted. A name with no
      // file behind it falls through and just fades the old bed out.
      const s = this.sounds.get(name);
      if (!this.loaded || (s !== undefined && s.urls.some((u) => !this.failed.has(u)))) {
        void this.decodeLazy(name).then(() => {
          if (this.wanted[kind] === name && this.beds[kind]?.name !== name && this.has(name)) this.bed(kind, name, fade);
        });
        return false;
      }
    }
    if (current) {
      current.gain.gain.setTargetAtTime(0, ctx.currentTime, fade / 3);
      current.src.stop(ctx.currentTime + fade + 0.1);
      if (current.layer) {
        current.layer.gain.gain.setTargetAtTime(0, ctx.currentTime, fade / 3);
        current.layer.src.stop(ctx.currentTime + fade + 0.1);
      }
      this.beds[kind] = null;
      if (isLazy(current.name)) setTimeout(() => this.release(current.name), (fade + 0.5) * 1000);
    }
    if (name === null) return true;
    if (pair) return this.startLayers(kind, name, pair, fade);
    const picked = this.pick(name);
    if (!picked) return false;
    const src = ctx.createBufferSource();
    src.buffer = picked.buffer;
    src.loop = !picked.sound.once;
    if (src.loop) trimLoop(src, picked.sound);
    const g = ctx.createGain();
    g.gain.value = 0;
    g.gain.setTargetAtTime(picked.gain, ctx.currentTime, fade / 3);
    src.connect(g);
    g.connect(kind === 'music' ? this.musicOut(ctx, bus) : bus);
    src.start();
    this.beds[kind] = { name, src, gain: g };
    return true;
  }

  /**
   * Two music beds cut to the same loop, started on the same clock tick so
   * they stay in step and crossfaded by `mix` (0 = all the first, 1 = all
   * the second): story mode's explore and fight layers. The same pair again
   * only moves the mix, over `seconds`.
   */
  layers(a: string, b: string, mix: number, seconds = 2.5): boolean {
    this.layerMix = clamp01(mix);
    const name = `${a}+${b}`;
    const cur = this.beds.music;
    const ctx = this.synth.context;
    if (cur && cur.name === name && cur.layer && ctx) {
      this.wanted.music = name;
      cur.gain.gain.setTargetAtTime(cur.layer.peak[0] * (1 - this.layerMix), ctx.currentTime, seconds / 3);
      cur.layer.gain.gain.setTargetAtTime(cur.layer.peak[1] * this.layerMix, ctx.currentTime, seconds / 3);
      return true;
    }
    return this.bed('music', name);
  }

  private startLayers(kind: 'music' | 'ambience', name: string, pair: [string, string], fade: number): boolean {
    const ctx = this.synth.context;
    const bus = this.synth.bus(kind);
    const a = this.pick(pair[0]), b = this.pick(pair[1]);
    if (!ctx || !bus || !a || !b) return false;
    const out = kind === 'music' ? this.musicOut(ctx, bus) : bus;
    const make = (p: NonNullable<ReturnType<GameAudio['pick']>>, level: number): { src: AudioBufferSourceNode; gain: GainNode } => {
      const src = ctx.createBufferSource();
      src.buffer = p.buffer;
      src.loop = true;
      trimLoop(src, p.sound);
      const g = ctx.createGain();
      g.gain.value = 0;
      g.gain.setTargetAtTime(level, ctx.currentTime, fade / 3);
      src.connect(g);
      g.connect(out);
      return { src, gain: g };
    };
    const first = make(a, a.gain * (1 - this.layerMix));
    const second = make(b, b.gain * this.layerMix);
    const t = ctx.currentTime + 0.05;
    first.src.start(t);
    second.src.start(t);
    this.beds[kind] = { name, src: first.src, gain: first.gain, layer: { src: second.src, gain: second.gain, peak: [a.gain, b.gain] } };
    return true;
  }

  /** The music bus's duck stage (created on first use), where every music bed connects. */
  private musicOut(ctx: AudioContext, bus: AudioNode): GainNode {
    if (!this.duckGain) {
      this.duckGain = ctx.createGain();
      this.duckGain.connect(bus);
    }
    return this.duckGain;
  }

  /** Decode a bed's files on demand (while the manifest is still loading there is nothing to do; `load` picks it up). */
  private async decodeLazy(name: string): Promise<void> {
    const s = this.sounds.get(name);
    if (!s) return;
    await Promise.all(s.urls.map((u) => this.decode(u)));
  }

  /** Drop a lazy bed's decoded audio once it has faded out and nothing wants it. */
  private release(name: string): void {
    const pair = pairOf(name);
    if (pair) {
      if (this.beds.music?.name === name || this.wanted.music === name) return;
      for (const n of pair) this.release(n);
      return;
    }
    if (this.beds.music?.name === name || this.beds.ambience?.name === name) return;
    if (this.wanted.music === name || this.wanted.ambience === name) return;
    const s = this.sounds.get(name);
    if (s) for (const u of s.urls) this.buffers.delete(u);
  }

  /** The bed playing on a bus, if any. */
  playing(kind: 'music' | 'ambience'): string | null {
    return this.beds[kind]?.name ?? null;
  }

  /** Dip the music under a sting, then let it back up. */
  duck(amount = 0.4, seconds = 0.8): void {
    const ctx = this.synth.context;
    if (!ctx || !this.duckGain) return;
    const t = ctx.currentTime;
    this.duckGain.gain.cancelScheduledValues(t);
    this.duckGain.gain.setTargetAtTime(1 - clamp01(amount), t, 0.03);
    this.duckGain.gain.setTargetAtTime(1, t + seconds, 0.25);
  }

  /** Fade every bed out (leaving a room). */
  stopBeds(fade = BED_FADE): void {
    this.bed('music', null, fade);
    this.bed('ambience', null, fade);
  }
}

let singleton: GameAudio | null = null;

/** The one GameAudio of the page. */
export function getAudio(): GameAudio {
  if (!singleton) singleton = new GameAudio();
  return singleton;
}
